'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Navigation from '../../../components/Navigation';
import RelatedPages from '../../../components/RelatedPages';
import { GuideEyebrow, GuideSection, GuideSubtitle, GuideTitle } from '../../../components/GuideHeading';

const Code = ({ children }: { children: string }) => (
    <Box
        component="pre"
        sx={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            p: 2,
            mb: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
        }}
    >
        {children}
    </Box>
);

export default function JsonPatchInJavaScriptGuide() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" className="main-container">
                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        <GuideEyebrow>Guide</GuideEyebrow>
                        <GuideTitle>JSON Patch in JavaScript</GuideTitle>
                        <GuideSubtitle>
                            How to apply RFC 6902 JSON Patches in Node.js and the browser - with fast-json-patch, immutable alternatives, and diff generation between two documents.
                        </GuideSubtitle>

                        <GuideSection>Choosing a library</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            The most widely used implementation is <code>fast-json-patch</code>. It covers every RFC 6902 operation (add, remove, replace, move, copy, test) and works in Node and the browser:
                        </Typography>
                        <Code>{`npm install fast-json-patch`}</Code>

                        <GuideSection>Applying a patch</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>applyPatch</code> returns a result object whose <code>newDocument</code> holds the patched document. By default it mutates in place - pass <code>false</code> as the third argument and a cloned document is returned instead:
                        </Typography>
                        <Code>{`import { applyPatch } from 'fast-json-patch';

const doc = {
  name: 'demo',
  version: 1,
  tags: ['a', 'b'],
};

const patch = [
  { op: 'replace', path: '/version', value: 2 },
  { op: 'add', path: '/tags/-', value: 'c' },
  { op: 'remove', path: '/name' },
];

const result = applyPatch(doc, patch, true, false);
console.log(result.newDocument);
// { version: 2, tags: ['a', 'b', 'c'] }`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            The path <code>/tags/-</code> appends to the array. Paths are JSON Pointers (RFC 6901): escape <code>~</code> as <code>~0</code> and <code>/</code> as <code>~1</code> inside keys.
                        </Typography>

                        <GuideSection>Generating a patch from two documents</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>compare</code> produces the operations that turn one document into the other - the same output as the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link>:
                        </Typography>
                        <Code>{`import { compare } from 'fast-json-patch';

const before = { a: 1, b: { c: 2 } };
const after = { a: 1, b: { c: 3, d: 4 } };

console.log(compare(before, after));
// [
//   { op: 'replace', path: '/b/c', value: 3 },
//   { op: 'add', path: '/b/d', value: 4 },
// ]`}</Code>

                        <GuideSection>Guarding with the test operation</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            A failing test operation stops evaluation at that point. Use it as a precondition so a patch only lands on the state it was built for:
                        </Typography>
                        <Code>{`const patch = [
  { op: 'test', path: '/version', value: 1 },
  { op: 'replace', path: '/version', value: 2 },
];

try {
  applyPatch(doc, patch, true);
} catch (err) {
  // JsonPatchError: test failed, path missing, or type mismatch
  console.error('patch rejected:', err.message);
}`}</Code>

                        <GuideSection>Common pitfalls</GuideSection>
                        <Typography component="ul" sx={{ color: 'var(--fg-muted)', mb: 3, pl: 3 }}>
                            <li><strong>Mutation surprises.</strong> <code>applyPatch(doc, patch)</code> mutates <code>doc</code> unless you request a clone. In React state, always use the immutable form or you will miss re-renders.</li>
                            <li><strong>Strict test equality.</strong> A test on <code>1</code> fails against <code>'1'</code>. JSON types must match exactly.</li>
                            <li><strong>Array indices shift after remove.</strong> Removing index 0 renumbers everything after it. Remove from the highest index down, or guard with test operations.</li>
                            <li><strong>Evaluation stops on error.</strong> RFC 6902 does not guarantee rollback of earlier operations; check the library&apos;s mutation behavior before treating a patch as a transaction.</li>
                        </Typography>

                        <GuideSection>Prototype patches visually</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Paste your two documents into the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> to get the exact operations, or inspect changes line by line with the{' '}
                            <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON Diff tool</Link>. Then hand the patch to <code>applyPatch</code> in your app or API client.
                        </Typography>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 4 }}>
                            Background on the format: <Link href="/docs" style={{ color: 'var(--accent)' }}>JSON Patch documentation</Link> and{' '}
                            <Link href="/examples" style={{ color: 'var(--accent)' }}>real-world examples</Link>.
                        </Typography>

                        <RelatedPages currentPage="docs" />
                    </Box>
                </Container>
            </main>
        </>
    );
}
